import { useCallback } from 'react';
import type { StoryPhoto } from '../components/StoryGallery';

type UseNarrationAdvanceOptions = {
  narrationEnabled: boolean;
  activePhotos: StoryPhoto[];
  activePhotoIndex: number;
  activeIndex: number;
  milestonesCount: number;
  activeMilestoneYear: string;
  stopAudio: () => void;
  goNext: () => void;
  advancePhoto: () => { status: 'empty' | 'end' | 'advanced'; nextPhoto?: StoryPhoto };
  setNarrationEnabled: (value: boolean) => void;
  setCurrentPhoto: (photo: StoryPhoto | null) => void;
  setActivePhotoIndex: (index: number) => void;
};

export const useNarrationAdvance = ({
  narrationEnabled,
  activePhotos,
  activePhotoIndex,
  activeIndex,
  milestonesCount,
  activeMilestoneYear,
  stopAudio,
  goNext,
  advancePhoto,
  setNarrationEnabled,
  setCurrentPhoto,
  setActivePhotoIndex,
}: UseNarrationAdvanceOptions) => {
  const handleNarrationAdvance = useCallback(() => {
    if (!narrationEnabled) return;
    if (activePhotos.length === 0) return;
    if (activePhotoIndex >= activePhotos.length - 1) {
      if (activeIndex >= milestonesCount - 1) {
        setNarrationEnabled(false);
        stopAudio();
        return;
      }
      goNext();
      return;
    }
    const result = advancePhoto();
    if (result.status === 'advanced' && result.nextPhoto) {
      setCurrentPhoto(result.nextPhoto);
    }
  }, [
    narrationEnabled,
    activePhotos,
    activePhotoIndex,
    activeIndex,
    milestonesCount,
    stopAudio,
    goNext,
    advancePhoto,
    setNarrationEnabled,
    setCurrentPhoto,
  ]);

  const handlePhotoChange = useCallback(
    (photo: StoryPhoto, index: number, instanceId?: string) => {
      if (instanceId && instanceId !== activeMilestoneYear) return;
      setCurrentPhoto(photo);
      setActivePhotoIndex(index);
    },
    [activeMilestoneYear, setActivePhotoIndex, setCurrentPhoto]
  );

  return { handleNarrationAdvance, handlePhotoChange };
};
